// Display text used across screens and the HUD: modes, maps, phases, items, mutators, awards,
// kill-feed verbs (§11.6), personalities, challenges (§7.4) and control glyph names (§8).
import { TICK_HZ, TUNE } from '@shared/constants';
import { AwardId, Cause, Difficulty, ItemKind, ModeId, MutatorId, Personality, RoundPhase, type MapId } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import { BASE_URL } from '../core/base';

export const MODE_NAMES: Readonly<Record<ModeId, string>> = {
  [ModeId.LOBBY]: 'Lobby', [ModeId.PODIUM]: 'Podium', [ModeId.ROYALE]: 'Bonk Royale',
  [ModeId.HOT_POTATO]: 'Hot Potato', [ModeId.CROWN_GRAB]: 'Crown Grab',
};

/** One-line rules for vote cards and Practice. */
export const MODE_BLURBS: Readonly<Record<ModeId, string>> = {
  [ModeId.LOBBY]: 'Hang out and warm up',
  [ModeId.PODIUM]: 'Knock the champ off!',
  [ModeId.ROYALE]: 'One life. Last bean standing.',
  [ModeId.HOT_POTATO]: "Pass the bomb before it blows!",
  [ModeId.CROWN_GRAB]: `Hold the crown for ${TUNE.crown.goalTicks / TICK_HZ} s.`,
};

export function mapName(id: MapId): string {
  return MAPS[id].name;
}

export const PHASE_NAMES: Readonly<Record<RoundPhase, string>> = {
  [RoundPhase.INTRO]: 'GET READY', [RoundPhase.CALM]: 'CALM', [RoundPhase.RAMP]: 'HAZARDS UP',
  [RoundPhase.SUDDEN_DEATH]: 'SUDDEN DEATH', [RoundPhase.OVER]: 'TIME',
};

export const ITEM_NAMES: Readonly<Record<ItemKind, string>> = {
  [ItemKind.NONE]: '', [ItemKind.BANANA]: 'Banana Peel', [ItemKind.BOMB]: 'Bonk Bomb', [ItemKind.GLOVE]: 'Boxing Glove',
  [ItemKind.BALLOON]: 'Balloon', [ItemKind.PLUNGER]: 'Plunger', [ItemKind.SHAKE]: 'Big Shake',
};

export const ITEM_BLURBS: Readonly<Record<ItemKind, string>> = {
  [ItemKind.NONE]: '', [ItemKind.BANANA]: 'Drop 3 slippery peels behind you',
  [ItemKind.BOMB]: 'Lob it, then run', [ItemKind.GLOVE]: 'Wind up a huge POW!',
  [ItemKind.BALLOON]: 'Float to safety (saves a fall!)', [ItemKind.PLUNGER]: 'Yank a bean to you',
  [ItemKind.SHAKE]: 'Go BIG for 6 seconds',
};

export const MUTATOR_NAMES: Readonly<Record<MutatorId, string>> = {
  [MutatorId.NONE]: 'No thanks', [MutatorId.BUTTER_FLOOR]: 'Butter Floor', [MutatorId.FLOATY]: 'Floaty',
  [MutatorId.BOMB_PARTY]: 'Bomb Party', [MutatorId.DOUBLE_DIZZY]: 'Double Dizzy',
};

export const MUTATOR_BLURBS: Readonly<Record<MutatorId, string>> = {
  [MutatorId.NONE]: 'Play it straight',
  [MutatorId.BUTTER_FLOOR]: 'Everything slides',
  [MutatorId.FLOATY]: 'Low gravity, big hits',
  [MutatorId.BOMB_PARTY]: 'Every crate is a bomb',
  [MutatorId.DOUBLE_DIZZY]: 'Dizzy builds twice as fast',
};

export const PLAYLIST_NAMES: Readonly<Record<string, string>> = {
  party: 'Party Mix', royale: 'Royale only', potato: 'Hot Potato only', crown: 'Crown Grab only',
};

export const TEAMS_NAMES: Readonly<Record<string, string>> = { off: 'Off', split: 'Split', humansVsBots: 'Humans vs Bots' };

export const DIFFICULTY_NAMES: Readonly<Record<Difficulty, string>> = {
  [Difficulty.CHILL]: 'Chill', [Difficulty.NORMAL]: 'Normal', [Difficulty.SPICY]: 'Spicy',
};

export const PERSONALITY_NAMES: Readonly<Record<Personality, string>> = {
  [Personality.BULLY]: 'Bully', [Personality.OPPORTUNIST]: 'Opportunist', [Personality.COWARD]: 'Coward',
  [Personality.CHAOS]: 'Chaos Goblin', [Personality.TACTICIAN]: 'Tactician',
};

/** Lobby tooltip text per personality (§10.5). */
export const PERSONALITY_BLURBS: Readonly<Record<Personality, string>> = {
  [Personality.BULLY]: 'Picks on the leader',
  [Personality.OPPORTUNIST]: 'Steals your KOs',
  [Personality.COWARD]: 'Hugs the middle, throws stuff',
  [Personality.CHAOS]: 'Shoves anything nearby',
  [Personality.TACTICIAN]: 'Grabs, clashes, mind games',
};

/** "1st", "2nd", "3rd", "4th" … */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** m:ss */
export function clockText(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Share link for a room code (§9.2: https://host/r/KDFT). */
export function roomLink(code: string): string {
  return `${location.origin}${BASE_URL}r/${code}`;
}

export const AWARD_NAMES: Readonly<Record<AwardId, string>> = {
  [AwardId.BIG_BONKER]: 'BIG BONKER', [AwardId.BANANA_BARON]: 'BANANA BARON', [AwardId.CLUMSIEST]: 'CLUMSIEST BEAN',
  [AwardId.GHOST_MENACE]: 'GHOST MENACE', [AwardId.HONK_LORD]: 'HONK LORD', [AwardId.NINE_LIVES]: 'NINE LIVES',
  [AwardId.FREQUENT_FLYER]: 'FREQUENT FLYER', [AwardId.OLDEST_PEEL]: 'OLDEST PEEL', [AwardId.GRAB_GOBLIN]: 'GRAB GOBLIN',
  [AwardId.CROWN_HOGGER]: 'CROWN HOGGER',
};

/** What an award's value means (§9.8), e.g. "6 KOs". */
export function awardValueText(id: AwardId, value: number): string {
  const n = Math.round(value);
  switch (id) {
    case AwardId.BIG_BONKER: return `${n} KO${n === 1 ? '' : 's'}`;
    case AwardId.BANANA_BARON: return `${n} peel slip${n === 1 ? '' : 's'}`;
    case AwardId.CLUMSIEST: return `${n} self-fall${n === 1 ? '' : 's'}`;
    case AwardId.GHOST_MENACE: return `${n} ghost points`;
    case AwardId.HONK_LORD: return `${n} honks`;
    case AwardId.NINE_LIVES: return `${n} saves`;
    case AwardId.FREQUENT_FLYER: return `${value.toFixed(1)} m flight`;
    case AwardId.OLDEST_PEEL: return `peel aged ${(value / 60).toFixed(1)} s`;
    case AwardId.GRAB_GOBLIN: return `${n} throws`;
    case AwardId.CROWN_HOGGER: return `${n} s with the crown`;
    default: return String(n);
  }
}

export const AWARD_BLURBS: Readonly<Record<AwardId, string>> = {
  [AwardId.BIG_BONKER]: 'Most KOs', [AwardId.BANANA_BARON]: 'Most peel slips caused', [AwardId.CLUMSIEST]: 'Fell off all by themselves',
  [AwardId.GHOST_MENACE]: 'Haunted the living', [AwardId.HONK_LORD]: 'Would not stop honking', [AwardId.NINE_LIVES]: 'Refused to fall',
  [AwardId.FREQUENT_FLYER]: 'Longest single flight', [AwardId.OLDEST_PEEL]: 'A vintage banana', [AwardId.GRAB_GOBLIN]: 'Most throws',
  [AwardId.CROWN_HOGGER]: 'Hogged that crown',
};

/** Kill-feed verbs by cause (§11.6). */
export const KILL_VERBS: Readonly<Record<number, string>> = {
  [Cause.SHOVE]: 'BONKED', [Cause.THROW]: 'YEETED', [Cause.GLOVE]: 'POWED', [Cause.BOMB]: 'KABOOMED',
  [Cause.PEEL]: 'SLIPPED', [Cause.PLUNGER]: 'PLUNGED', [Cause.STOMP]: 'STOMPED', [Cause.PEBBLE]: 'PEBBLED',
  [Cause.BUMPER]: 'PINBALLED', [Cause.FLIPPER]: 'PINBALLED', [Cause.PINBALL]: 'PINBALLED', [Cause.SLINGSHOT]: 'PINBALLED',
  [Cause.SWEEPER]: 'SWEPT', [Cause.FLAME]: 'TOASTED', [Cause.CUP]: 'SPUN OUT', [Cause.POTATO]: 'POTATOED',
  [Cause.BODY]: 'BOOPED', [Cause.BUBBLE]: 'BOOPED', [Cause.CRATE]: 'BONKED', [Cause.CLASH]: 'BONKED',
};
export const SELF_VERB = 'SELF-YEETED';

/** The §11.6 verb for a KO's cause; self-falls use SELF-YEETED. */
export function killVerb(cause: number, self: boolean): string {
  return self ? SELF_VERB : KILL_VERBS[cause] ?? 'BONKED';
}

/** Errors that answer a join, create or Quick Play request. */
export const JOIN_ERROR_TEXT: Readonly<Record<string, string>> = {
  ROOM_NOT_FOUND: 'Room not found',
  ROOM_FULL: 'Room full — join as spectator?',
  SERVER_FULL: 'The server is full — try again soon',
};

/** Toasts for `kicked` reasons (§9.6, §21.7). */
export const KICK_TOASTS: Readonly<Record<string, string>> = {
  host: 'The host removed you from the room',
  afk: 'Kicked for being idle',
  abuse: 'Disconnected by the server',
};

/** Toasts for errors that do not end a join. */
export const ERROR_TOASTS: Readonly<Record<string, string>> = {
  RATE_LIMITED: 'Whoa, slow down!',
  NOT_HOST: 'Only the host can do that',
  BAD_REQUEST: 'The server did not understand that',
};

/** Emote names by id 1–4 (§7.3). */
export const EMOTE_NAMES: readonly string[] = ['', 'Wave', 'Dance', 'Laugh', 'Flex'];

/** Bean Level unlock reward lines and the like: "Level 12". */
export function levelText(level: number): string {
  return `Level ${level}`;
}
